"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Search, X } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useTranslation } from "@/lib/react/i18n";
import { ImRadioChecked } from "react-icons/im";
import { IoRadioButtonOnSharp } from "react-icons/io5";

export type UserSelectItem = { id: string; name: string };
type SelectMode = "multi" | "single";

type Props<T extends UserSelectItem> = {
  title: string;
  placeholder: string;
  isFormMode: boolean;

  items: T[];
  value: string[];
  onChange: (next: string[]) => void;

  searchValue?: string;
  onSearchCommit?: (q: string) => void;

  loadMore?: () => void;
  hasMore?: boolean;

  buttonLabelEmpty?: string;
  buttonLabelFilled?: string;
  showChips?: boolean;
  getLabel?: (item: T) => string;

  mode?: SelectMode;
  closeOnSelect?: boolean;

  scrollAreaId?: string;
  triggerTestId?: string;
  sheetTestId?: string;
};

export function UserMultiSelectBottomSheet<T extends UserSelectItem>({
  title,
  placeholder,
  items,
  value,
  onChange,

  searchValue = "",
  onSearchCommit,

  loadMore,
  hasMore = false,

  buttonLabelEmpty = "Select",
  buttonLabelFilled = "Edit selection",
  showChips = true,
  getLabel,
  isFormMode = true,

  mode = "multi",
  closeOnSelect = mode === "single",
  scrollAreaId = "bottomSheetScrollArea",
  triggerTestId,
  sheetTestId,
}: Props<T>) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const searchBtnRef = useRef<HTMLButtonElement | null>(null);
  const clearBtnRef = useRef<HTMLButtonElement | null>(null);

  const [queryDraft, setQueryDraft] = useState(searchValue);
  const [temp, setTemp] = useState<string[]>(value);

  const committed = searchValue.trim();
  const draft = queryDraft.trim();
  const isDirtyQuery = !!onSearchCommit && draft !== committed;
  const isNoResult = !!onSearchCommit && draft.length > 0 && items.length === 0;

  const isEmptySelection = temp.length === 0;
  const disableWhenEmpty = isFormMode && isEmptySelection;

  const sameSelection = useMemo(() => {
    if (temp.length !== value.length) return false;
    const a = [...temp].map(String).sort();
    const b = [...value].map(String).sort();
    return a.every((v, i) => v === b[i]);
  }, [temp, value]);

  const isApplyDisabled = isDirtyQuery || isNoResult || disableWhenEmpty || sameSelection;

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const labelOf = useCallback((item: T) => (getLabel ? getLabel(item) : item.name), [getLabel]);

  const itemByIdRef = useRef<Map<string, T>>(new Map());

  useEffect(() => {
    for (const it of items) {
      itemByIdRef.current.set(String(it.id), it);
    }
  }, [items]);

  const selectedItems = useMemo(() => {
    return value
      .map((id) => itemByIdRef.current.get(String(id)))
      .filter(Boolean) as T[];
  }, [value]);

  const openSheet = () => {
    setTemp(value);
    setQueryDraft(searchValue);
    setOpen(true);
  };

  const commitSearch = useCallback(() => {
    if (!onSearchCommit) return;
    onSearchCommit(queryDraft.trim());
  }, [onSearchCommit, queryDraft]);

  const selectItem = useCallback(
    (id: string) => {
      const key = String(id);

      if (mode === "single") {
        const next = [key];
        if (closeOnSelect) {
          onChange(next);
          setOpen(false);
          return;
        }
        setTemp(next);
        return;
      }

      setTemp((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
    },
    [mode, closeOnSelect, onChange]
  );

  const apply = useCallback(() => {
    if (onSearchCommit) {
      const committedNow = searchValue.trim();
      const draftNow = queryDraft.trim();
      if (draftNow !== committedNow) {
        onSearchCommit(draftNow);
        return;
      }
      if (draftNow.length > 0 && items.length === 0) return;
    }

    onChange(temp);
    setOpen(false);
  }, [onChange, temp, onSearchCommit, searchValue, queryDraft, items]);

  const cancel = useCallback(() => {
    setOpen(false);
  }, []);

  const removeChip = (id: string) => {
    const key = String(id);
    if (mode === "single") {
      onChange([]);
      return;
    }
    onChange(value.filter((x) => x !== key));
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, cancel]);

  const enableInfinite = !!loadMore;

  return (
    <div className="space-y-2">
      <button
        type="button"
        data-testid={triggerTestId}
        onClick={openSheet}
        className={
          isFormMode
            ? "w-full text-left px-4 py-3 rounded border border-gray-300 bg-white text-black"
            : "shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 text-white/80 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
        }
      >
        {selectedItems.length > 0 ? buttonLabelFilled : buttonLabelEmpty}
      </button>

      {showChips && selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2">
          {selectedItems.map((it) => (
            <span
              key={it.id}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-sm"
            >
              {labelOf(it)}
              <button
                type="button"
                onClick={() => removeChip(it.id)}
                className="text-white/80 hover:text-white"
                aria-label={`remove-${it.id}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {mounted && open
        ? createPortal(
          <div className="fixed inset-0 z-[9999]" data-testid={sheetTestId}>
            <div className="absolute inset-0 bg-black/60" />

            <div
              className="absolute inset-0 flex items-end sm:items-center justify-center p-3 sm:p-6"
              onClick={cancel}
              onPointerDown={cancel}
              role="presentation"
            >
              <div
                className="
                    w-full sm:max-w-lg
                    rounded-t-2xl sm:rounded-2xl
                    bg-[#111] border border-white/10
                    p-4
                    max-h-[80vh] overflow-hidden
                    shadow-2xl
                  "
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-white font-bold text-lg">{title}</h2>
                  <button
                    type="button"
                    onClick={cancel}
                    className="text-white/70 hover:text-white text-xl"
                    aria-label="close"
                  >
                    ×
                  </button>
                </div>

                {onSearchCommit && (
                  <div className="mb-3">
                    <div className="relative">
                      <button
                        ref={searchBtnRef}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          commitSearch();
                        }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-white/10"
                        aria-label="search"
                      >
                        <Search className="w-4 h-4 text-white/60" />
                      </button>

                      <input
                        value={queryDraft}
                        onChange={(e) => setQueryDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            commitSearch();
                          }
                        }}
                        placeholder={placeholder}
                        className="w-full pl-12 pr-12 px-4 py-3 rounded-full bg-white/10 text-white placeholder:text-white/40 outline-none"
                        onBlur={(e) => {
                          const next = e.relatedTarget as HTMLElement | null;
                          if (next && (next === searchBtnRef.current || next === clearBtnRef.current)) return;
                          commitSearch();
                        }}
                      />

                      {queryDraft.trim().length > 0 && (
                        <button
                          ref={clearBtnRef}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setQueryDraft("");
                            onSearchCommit("");
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-white/10"
                          aria-label="clear"
                        >
                          <X className="w-4 h-4 text-white/60" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div id={scrollAreaId} className="overflow-auto max-h-[50vh] rounded-lg border border-white/10">
                  <InfiniteScroll
                    dataLength={items.length}
                    next={() => loadMore?.()}
                    hasMore={enableInfinite ? !!hasMore : false}
                    loader={<></>}
                    scrollableTarget={scrollAreaId}
                  >
                    {items.map((it) => {
                      const checked = temp.includes(String(it.id));

                      return (
                        <label
                          key={it.id}
                          className="flex items-center justify-between px-4 py-4 border-b border-white/5 cursor-pointer select-none"
                          onClick={(e) => {
                            e.preventDefault();
                            selectItem(it.id);
                          }}
                        >
                          <span className="text-white">{labelOf(it)}</span>

                          <input
                            type={mode === "single" ? "radio" : "checkbox"}
                            checked={checked}
                            onChange={() => selectItem(it.id)}
                            className="sr-only"
                          />

                          {mode === "single" ? (
                            checked ? (
                              <ImRadioChecked className="text-purple-400 text-[22px]" aria-hidden="true" />
                            ) : (
                              <IoRadioButtonOnSharp
                                className="text-white/40 hover:text-purple-300 transition text-[22px]"
                                aria-hidden="true"
                              />
                            )
                          ) : (
                            <span
                              className="
                                  grid place-items-center
                                  h-7 w-7 rounded-full
                                  border border-white/25
                                  bg-white/5
                                  transition
                                "
                              aria-hidden="true"
                            >
                              <Check
                                className={[
                                  "h-4 w-4 text-purple-200 transition",
                                  checked ? "opacity-100 scale-100" : "opacity-0 scale-75",
                                ].join(" ")}
                                strokeWidth={3}
                              />
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </InfiniteScroll>

                  {items.length === 0 && <div className="p-4 text-white/60">{t("No search results found")}</div>}
                </div>

                {!(mode === "single" && closeOnSelect) && (
                  <div>
                    {isDirtyQuery && (
                      <div className="mt-2 text-xs text-red-400 flex justify-center">
                        {t("Confirm your search with Enter/Search")}
                      </div>
                    )}
                    <div className="mt-4 flex gap-3">
                      <button
                        type="button"
                        onClick={apply}
                        disabled={isApplyDisabled}
                        className={[
                          "flex-1 py-3 rounded-full",
                          isApplyDisabled
                            ? "bg-purple-500/40 text-white/60 cursor-not-allowed"
                            : "bg-purple-500 text-white",
                        ].join(" ")}
                      >
                        {t("Apply")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
}